import React, { useEffect, useMemo, useState } from 'react';

import { Table, Tbody, Td } from '@chakra-ui/react';
import { Collapse, TableContainer } from '@material-ui/core';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Loader } from 'screens/analytics/commonComponents';
import styled from 'styled-components';
import { StyledBorderedPaper } from 'styles/common';

import { TableBodyRow, TableHeaderRow } from 'uiToolkitV1/TableComponents';
import { TableHeaderCell } from 'uiToolkitV1/TableComponents/TableHeader';
import { TableHead } from 'uiToolkitV1/TableComponents/TableHeader/TableHead';

import { useLevelUpAnalytics } from '../context';
import { columnToCellMetaMapper, getFlexJustifyFromCellAlign } from '../helpers';
import { useFetchLevelUpAnalyticsData } from '../hooks/dataHooks';

const SubGroupedByFunctionTable = ({
  functionId,
  row,
  rowProps,
  visibleColumns,
  depth: parentDepth,
  levelsForWhichLineToBeDrawn = [],
  isParentLastRow,
  setTableContainerWidth,
}) => {
  const {
    state: {
      currentState: { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedDateRange },
      selectedView: { expandedRowDetails },
    },
    setNoData,
  } = useLevelUpAnalytics();

  const { data, isLoading, isIdle } = useFetchLevelUpAnalyticsData({
    cohort: selectedCohort,
    groupByKeys: selectedGroupByKey,
    splitByKeys: selectedSplitByKey,
    dateRange: selectedDateRange,
    parentUuid: functionId,
  });

  const [expanded, setExpanded] = useState({});

  const columns = useMemo(
    () =>
      data?.columns.map(({ id, displayName, uuidType }) =>
        columnToCellMetaMapper({
          id,
          displayName,
          uuidType,
          isSubRow: true,
          parentDepth,
          levelsForWhichLineToBeDrawn,
          isParentLastRow,
        })
      ) ?? [],
    [data?.columns, isParentLastRow, levelsForWhichLineToBeDrawn, parentDepth]
  );

  useEffect(() => {
    if (parentDepth > 3) {
      setTableContainerWidth(1600);
    }
    return () => {
      if (parentDepth > 3) {
        setTableContainerWidth(1440);
      }
    };
  }, [parentDepth]);

  //   init react table
  const table = useReactTable({
    data: data?.values,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,

    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => Boolean(row?.original?.function?.value?.childFunctionIdsCount),
    debugTable: true,
  });

  const renderRowSubComponent = React.useCallback(({ row, rowProps, visibleColumns, rowsCount = 0 }) => {
    const functionId = row.original.function.uuid;
    const depth = parentDepth + 1;
    const isLastRow = row?.index === rowsCount - 1;
    const levels = [...levelsForWhichLineToBeDrawn, !isLastRow];

    return (
      <SubGroupedByFunctionTable
        functionId={functionId}
        row={row}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
        depth={depth}
        isParentLastRow={isLastRow}
        levelsForWhichLineToBeDrawn={levels}
        setTableContainerWidth={setTableContainerWidth}
      />
    );
  }, []);

  if (isLoading || isIdle) {
    return (
      <StyledBorderedPaper overFlow="auto" marginBottom="2rem" padding="0" topGutter={2}>
        <Loader />
      </StyledBorderedPaper>
    );
  }
  return (
    <StyledTableContainer style={{ overflow: 'visible' }}>
      <Table>
        <HiddenTableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <HiddenTableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const { width, fixedWidth, align } = header?.column?.columnDef ?? {};
                const justify = getFlexJustifyFromCellAlign(align);
                return (
                  <TableHeaderCell
                    size="small"
                    variant="head"
                    width={width}
                    fixedWidth={fixedWidth}
                    // minWidth={minWidth}
                    key={header.id}
                    colSpan={header.colSpan}
                    sx={{
                      div: { justifyContent: justify },
                    }}
                  />
                );
              })}
            </HiddenTableRow>
          ))}
        </HiddenTableHeader>
        <Tbody>
          {table.getRowModel().rows.map((row) => {
            const isRowExpanded = row?.getIsExpanded();
            const rowsCount = table.getRowModel()?.rows?.length;

            return (
              <>
                <TableBodyRow key={row.id} sx={{ borderBottom: '0 !important' }}>
                  {row.getVisibleCells().map((cell, index) => (
                    <Td key={cell.id} sx={{ position: 'relative', padding: '16px' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </TableBodyRow>

                {isRowExpanded && (
                  <TableBodyRow key={row.id}>
                    <Td
                      style={{
                        padding: 0,
                        border: 'unset',
                      }}
                      colSpan="10" // <- used to control width of sub-table
                      sx={{ position: 'relative', padding: '16px' }}
                    >
                      <Collapse in={isRowExpanded} timeout="0" unmountOnExit>
                        {renderRowSubComponent({ row, rowsCount })}
                      </Collapse>
                    </Td>
                  </TableBodyRow>
                )}
              </>
            );
          })}
        </Tbody>
      </Table>
    </StyledTableContainer>
  );
};

// const StyledTableRow = styled(TableRow)`
//   th,
//   td {
//     border-bottom: 1px solid ${props => props.theme.palette.borders.lightGrey};
//   }
//   ${props =>
//     props.hasBottomBorder &&
//     css`
//       border-bottom: 1px solid ${props.theme.palette.borders.lightGrey};
//     `}
// `;

// const StyledTableCell = styled(TableCell)`
//   position: relative;
//   border-left: ${props => props.borderLeft && `1px solid ${props.theme.palette.borders.lightGrey}`};
//   cursor: ${props => (props.isSortable ? 'pointer' : 'default')};
//   ${props =>
//     props.hoverable &&
//     css`
//       &:hover {
//         background-color: ${props => props.theme.palette.background.lightBlue};
//       }
//     `}
//     ${props =>
//       props.fixedWidth &&
//       css`
//         width: ${props.fixedWidth};
//       `}
//   ${props =>
//     props.minWidth &&
//     css`
//       min-width: ${props.minWidth};
//     `}
// `;

const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
`;

const HiddenTableHeader = styled(TableHead)`
  tr > th,
  tr > td {
    padding: 0;
    border-bottom: none !important;
  }
`;

const HiddenTableRow = styled(TableHeaderRow)`
  tr > th,
  tr > td {
    padding: 0;
    border-bottom: none !important;
  }
`;

export default SubGroupedByFunctionTable;
